"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { useChapterAudio } from "@/hooks/use-chapter-audio";
import { AudioPlayerProgress } from "./audio-player-progress";
import { AudioPlayerControls } from "./audio-player-controls";
import { AlertCircle, Play, X } from "lucide-react";
interface AudioPlayerProps {
  storySlug: string;
  chapterId: string;
  nextChapterId?: string;
  isAuthenticated?: boolean;
  autoPlay?: boolean;
  onChapterEnd?: () => void;
}
export function ChapterAudioPlayer(props) {
  const $ = _c(36);
  const audioState = useChapterAudio(props);
  const [isExpanded, setIsExpanded] = useState(props.autoPlay || false);
  let t0;
  let t1;
  if ($[0] !== props.autoPlay) {
    t0 = () => {
      if (props.autoPlay) {
        setIsExpanded(true);
      }
    };
    t1 = [props.autoPlay];
    $[0] = props.autoPlay;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (!isExpanded) {
    let t2;
    if ($[3] !== audioState) {
      t2 = () => {
        setIsExpanded(true);
        audioState.togglePlay();
      };
      $[3] = audioState;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== audioState.isLoading) {
      t3 = audioState.isLoading ? <AlertCircle className="size-6 animate-pulse ml-1" /> : <Play className="size-6 fill-current ml-1" />;
      $[5] = audioState.isLoading;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2 || $[8] !== t3) {
      t4 = <button onClick={t2} className="absolute right-4 md:right-8 bottom-full mb-4 z-50 flex items-center justify-center size-14 bg-primary text-primary-foreground shadow-xl shadow-primary/20 rounded-full hover:scale-105 active:scale-95 transition-all" aria-label="Open Audio Player">{t3}</button>;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsExpanded(false);
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <button onClick={t2} className="absolute -top-12 right-4 sm:right-8 p-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 transition-all z-50 rounded-full bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 flex items-center justify-center" aria-label="Close Player"><X className="size-5" strokeWidth={2.5} /></button>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== audioState.error || $[13] !== audioState.errorCode || $[14] !== audioState.isLoading) {
    t4 = audioState.error && !audioState.isLoading && <div className="absolute bottom-full left-0 right-0 mb-3 text-sm text-red-200 font-medium flex items-center justify-between animate-in fade-in slide-in-from-bottom-2 bg-red-950/80 backdrop-blur-md px-4 py-2.5 rounded-xl border border-red-500/40"><div className="flex items-center gap-2"><AlertCircle className="size-4 shrink-0" /><p className="truncate">{audioState.error}</p></div>{audioState.errorCode === "upgrade_required" && <a href="/reader-pricing" className="ml-4 shrink-0 hover:underline">Upgrade</a>}{audioState.errorCode === "login_required" && <a href="/signup" className="ml-4 shrink-0 hover:underline">Sign Up</a>}</div>;
    $[12] = audioState.error;
    $[13] = audioState.errorCode;
    $[14] = audioState.isLoading;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  let t6;
  if ($[16] !== audioState) {
    t5 = () => audioState.setIsDragging(true);
    t6 = () => audioState.setIsDragging(false);
    $[16] = audioState;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  let t7;
  if ($[19] !== audioState.audioUrl || $[20] !== audioState.currentTime || $[21] !== audioState.duration || $[22] !== audioState.handleSeek || $[23] !== t5 || $[24] !== t6) {
    t7 = <AudioPlayerProgress currentTime={audioState.currentTime} duration={audioState.duration} audioUrl={audioState.audioUrl} onSeek={audioState.handleSeek} onDragStart={t5} onDragEnd={t6} />;
    $[19] = audioState.audioUrl;
    $[20] = audioState.currentTime;
    $[21] = audioState.duration;
    $[22] = audioState.handleSeek;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== audioState.audioUrl || $[27] !== audioState.isLoading || $[28] !== audioState.isPlaying || $[29] !== audioState.skip || $[30] !== audioState.togglePlay) {
    t8 = <AudioPlayerControls isPlaying={audioState.isPlaying} isLoading={audioState.isLoading} audioUrl={audioState.audioUrl} onTogglePlay={audioState.togglePlay} onSkip={audioState.skip} />;
    $[26] = audioState.audioUrl;
    $[27] = audioState.isLoading;
    $[28] = audioState.isPlaying;
    $[29] = audioState.skip;
    $[30] = audioState.togglePlay;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t4 || $[33] !== t7 || $[34] !== t8) {
    t9 = <div className="absolute bottom-full left-0 right-0 z-40 bg-white/95 dark:bg-[#1f1d35]/95 backdrop-blur-md border-y border-slate-200/50 dark:border-slate-800/80 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] pt-3 pb-3 px-4 sm:px-6">{t3}<div className="max-w-3xl mx-auto flex flex-col gap-2 relative">{t4}{t7}{t8}</div></div>;
    $[32] = t4;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}